/**
 * <s-promo-popup> — opens its promo <dialog> once per visitor window.
 *
 * Attributes:
 *   delay           — seconds to wait before opening (default 2)
 *   frequency-days  — days before showing again after a dismissal; 0 = every visit
 *   storage-key     — localStorage key, changes with the promo code so a new promo re-shows
 *   force           — always open (editor preview), ignoring storage
 *
 * Links marked data-apply-code close the pop-up, then go through /discount/<code>.
 *
 * Commands (on the dialog):
 *   --copy-code — copy the text of [data-promo-code] to the clipboard
 */
class SPromoPopup extends HTMLElement {
  #ac;
  #timer;

  connectedCallback() {
    const dialog = this.querySelector('dialog');
    if (!dialog) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const key = this.getAttribute('storage-key') || 'promo-popup';

    dialog.addEventListener('command', (e) => {
      if (e.command === '--copy-code') this.#copy(e.source);
    }, opts);

    dialog.addEventListener('close', () => {
      try { localStorage.setItem(key, String(Date.now())); } catch (_) { /* storage unavailable */ }
    }, opts);

    // Links marked data-apply-code go through the discount route so the code is applied to the cart.
    this.addEventListener('click', (e) => {
      const link = e.target.closest('a[data-apply-code]');
      if (!link) return;
      // Close first: hides the pop-up right away and records the dismissal so it doesn't reopen on the next page.
      if (dialog.open) dialog.close();
      const code = this.dataset.code;
      if (!code) return;
      e.preventDefault();
      const root = (this.dataset.rootUrl || '/').replace(/\/$/, '');
      const target = new URL(link.href, location.href);
      location.href = `${root}/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(target.pathname + target.search)}`;
    }, opts);

    if (!this.hasAttribute('force') && !this.#shouldShow(key)) return;

    const delay = Math.max(0, Number(this.getAttribute('delay') || 2)) * 1000;
    this.#timer = setTimeout(() => {
      if (!dialog.open && !document.querySelector('dialog:modal')) dialog.showModal();
    }, delay);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#timer);
  }

  #shouldShow(key) {
    const days = Number(this.getAttribute('frequency-days') || 0);
    let last = null;
    try { last = localStorage.getItem(key); } catch (_) { return true; }
    if (!last || days <= 0) return true;
    return Date.now() - Number(last) > days * 86400000;
  }

  async #copy(button) {
    const code = this.querySelector('[data-promo-code]')?.textContent.trim();
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
    } catch (_) {
      return;
    }
    const label = button?.querySelector('[data-copy-label]');
    const status = this.querySelector('[data-copy-status]');
    if (label) {
      const original = label.textContent;
      label.textContent = this.dataset.copiedText || original;
      setTimeout(() => { label.textContent = original; }, 2000);
    }
    if (status) status.textContent = this.dataset.copiedText || '';
  }
}

if (!customElements.get('s-promo-popup')) customElements.define('s-promo-popup', SPromoPopup);
